import { v4 as uuidv4 } from "uuid";

import { getItineraryDetailsAPI } from "../getItineraryDetails";
import { saveItineraryDetailsAPI } from "./saveItineraryDetails";
import { hasUsableJWT } from "../../utils/authentication/hasUsableJWT";
import { consoleLog } from "../../utils/consoleLog/consoleLog";

// Clones a shared itinerary into a new row owned by the requesting user.
// Implemented over the legacy itinerary endpoints: GET the source row, then
// POST it under the new userId. Used by cloneSharedTravelPlanIfNeeded on the
// /shared/:id viewer's first interaction so the original stays untouched.
// Returns `{ data: { id } }` to match the prior travel-plans response shape.
export async function clonePublicTravelPlanAPI(accessJWT: string, sourceId: string, userId: string) {
  if (!hasUsableJWT(accessJWT, "clonePublicTravelPlanAPI")) return null;
  if (!sourceId || !userId) return null;

  const sourceRes: any = await getItineraryDetailsAPI(sourceId, accessJWT);
  const sourceData = sourceRes?.data;
  if (!sourceData) return null;

  const parsedMeta =
    typeof sourceData.metaData === "string"
      ? JSON.parse(sourceData.metaData)?.[0]
      : Array.isArray(sourceData.metaData)
        ? sourceData.metaData?.[0]
        : sourceData.metaData;
  const sourceSelectedChatData = parsedMeta?.selectedChatData;
  if (!sourceSelectedChatData) return null;

  // Generate a fresh UUID for the cloned row's primary key. Backend uses
  // TypeORM's `Itinerary.save(entity)` which treats a present `id` as "set
  // this primary key on insert" — passing "" caused the save to no-op and
  // echo the empty id back. The legacy save flow works because it passes
  // selectedChatData.id (the chat bubble UUID) for fresh trips.
  const newId = uuidv4();
  // Mirror the new id onto the inner selectedChatData.id too so the chat
  // bubble id and the saved Itinerary id stay aligned (matches the legacy
  // save flow's invariant that selectedChatData.id === Itinerary.id).
  const clonedSelectedChatData = { ...sourceSelectedChatData, id: newId };

  const clonePayload = {
    tripName: sourceData.tripName || "",
    userId,
    metaData: { selectedChatData: clonedSelectedChatData },
    status: "draft",
    numberOfDaysSelected: Number(sourceData.numberOfDaysSelected) || 0,
    year: "",
    selectedMonth: "",
    data: {},
    id: newId,
    historyId: "",
  };

  consoleLog(["CLONE ITINERARY PAYLOAD", clonePayload]);
  const cloneRes: any = await saveItineraryDetailsAPI(accessJWT, clonePayload, "POST");
  // eslint-disable-next-line no-console
  console.log("[CLONE-API] saveItineraryDetailsAPI returned (full):", cloneRes);
  // eslint-disable-next-line no-console
  console.log("[CLONE-API] generated newId =", newId, "| response message =", cloneRes?.message);
  // Treat any 200 / "saved" response as success — the id is the one we
  // sent (TypeORM uses the supplied UUID as the primary key on insert).
  const saveSucceeded =
    cloneRes?.statusCode === 200 ||
    cloneRes?.message === "Itineraries has been saved";
  if (!saveSucceeded) return null;
  consoleLog(["CLONE ITINERARY RESPONSE", cloneRes]);
  return { data: { id: newId } };
}
